/**
 * SettingsPrivacy — Iter 191t · Permissions › Privacy & Data
 *
 * Two responsibilities:
 *   1. Change your login credentials (email + password) with an email-
 *      confirmation loop.  Password change is instant; email change
 *      dispatches a confirmation link to the NEW address AND notifies
 *      the CURRENT address, and only completes once the link is clicked.
 *   2. Personal data lifecycle — download, do-not-sell, deletion.
 *
 * Backend:
 *   POST /api/auth/change-password
 *   POST /api/auth/change-email/request
 *   POST /api/auth/change-email/confirm  (public — token in URL)
 *   GET  /api/privacy/my-data
 *   DELETE /api/privacy/my-data
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, Mail, KeyRound, Loader2, Download, Ban, Trash2, ShieldCheck, MailCheck, Eye, EyeOff,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SettingsPrivacy() {
  const [me, setMe] = useState({ email: '', name: '' });

  // Password form
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  // Email form
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailRequested, setEmailRequested] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: auth() })
      .then(({ data }) => setMe({ email: data?.email || '', name: data?.name || data?.display_name || '' }))
      .catch(() => {});
  }, []);

  const submitPassword = async () => {
    if (!pwCurrent || !pwNew) {
      toast.error('Enter your current and new password');
      return;
    }
    if (pwNew.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error('Password confirmation does not match');
      return;
    }
    setPwSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        { headers: auth() },
      );
      toast.success(data?.message || 'Password updated');
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update password');
    } finally { setPwSaving(false); }
  };

  const submitEmail = async () => {
    if (!newEmail || !emailPw) {
      toast.error('Enter the new email address and your current password');
      return;
    }
    setEmailSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-email/request`,
        { new_email: newEmail.trim().toLowerCase(), current_password: emailPw },
        { headers: auth() },
      );
      toast.success(data?.message || 'Confirmation link sent to your new email');
      setEmailRequested(true);
      setEmailPw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not request email change');
    } finally { setEmailSaving(false); }
  };

  const downloadData = async () => {
    try {
      const res = await axios.get(`${API}/privacy/my-data`, { headers: auth() });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Personal data downloaded');
    } catch { toast.error('Failed to download data'); }
  };

  const requestDeletion = async () => {
    if (!(await themedConfirm('Are you sure? This will request permanent deletion of your account and all data.'))) return;
    try {
      const res = await axios.delete(`${API}/privacy/my-data`, { headers: auth() });
      toast.success(res.data.message || 'Deletion request submitted');
    } catch { toast.error('Failed to submit request'); }
  };

  return (
    <div className="relative" data-testid="settings-privacy-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div>
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Permissions &amp; Ownership</p>
          <h1 className="text-3xl font-bold text-white font-syne mt-1">Privacy &amp; Data</h1>
          <p className="text-sm text-white/65 mt-2 max-w-xl">
            Manage the login credentials on your EntrepreNatives account and how your personal data is
            handled. Sensitive changes always go through email confirmation.
          </p>
        </div>

        {/* Change password */}
        <GlassCard glow className="p-6" data-testid="privacy-password-card">
          <div className="flex items-start gap-4 mb-5">
            <GradientIconTile icon={KeyRound} ramp="violet" />
            <div className="flex-1">
              <h2 className="text-white font-bold font-syne">Change password</h2>
              <p className="text-xs text-white/55 mt-0.5">
                Minimum 8 characters. You&rsquo;ll get a confirmation email when it&rsquo;s done.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 relative">
              <Label className="text-[#94A3B8] text-xs">Current password</Label>
              <Input
                type={pwShow ? 'text' : 'password'}
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                placeholder="Your current password"
                className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl pr-12"
                data-testid="privacy-current-password"
              />
              <button
                type="button"
                onClick={() => setPwShow((v) => !v)}
                className="absolute right-3 top-9 text-white/50 hover:text-white"
                aria-label={pwShow ? 'Hide passwords' : 'Show passwords'}
                data-testid="privacy-toggle-pw-visibility"
              >
                {pwShow ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">New password</Label>
              <Input
                type={pwShow ? 'text' : 'password'}
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                data-testid="privacy-new-password"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Confirm new password</Label>
              <Input
                type={pwShow ? 'text' : 'password'}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder="Repeat new password"
                className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                data-testid="privacy-confirm-password"
              />
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <button
              onClick={submitPassword}
              disabled={pwSaving}
              className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-[0.16em] bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all flex items-center gap-2"
              data-testid="privacy-submit-password"
            >
              {pwSaving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {pwSaving ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </GlassCard>

        {/* Change email */}
        <GlassCard className="p-6" data-testid="privacy-email-card">
          <div className="flex items-start gap-4 mb-5">
            <GradientIconTile icon={Mail} ramp="cyan" />
            <div className="flex-1">
              <h2 className="text-white font-bold font-syne">Change email address</h2>
              <p className="text-xs text-white/55 mt-0.5">
                We&rsquo;ll email a confirmation link to the new address and notify your current one
                — the change only applies once you click that link.
              </p>
            </div>
          </div>

          {emailRequested ? (
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-start gap-3">
              <MailCheck size={18} className="text-emerald-300 mt-0.5" />
              <div className="text-sm text-emerald-100">
                Check <strong className="text-white">{newEmail}</strong> for the confirmation link.
                It expires in 60 minutes. Once you click it, you&rsquo;ll sign in with the new address.
                <button
                  type="button"
                  onClick={() => { setEmailRequested(false); setNewEmail(''); }}
                  className="ml-3 underline text-white/80 hover:text-white text-xs"
                  data-testid="privacy-reset-email-flow"
                >
                  Change another
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8] text-xs">Current email</Label>
                <Input value={me.email} disabled className="mt-1.5 bg-white/[0.03] border-white/10 text-white/70 rounded-xl" data-testid="privacy-current-email" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">New email</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@newdomain.com"
                  className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                  data-testid="privacy-new-email"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#94A3B8] text-xs">Confirm with your current password</Label>
                <Input
                  type="password"
                  value={emailPw}
                  onChange={(e) => setEmailPw(e.target.value)}
                  placeholder="Current password"
                  className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                  data-testid="privacy-email-password"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={submitEmail}
                  disabled={emailSaving}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-[0.16em] bg-white/10 hover:bg-white/15 text-white border border-white/15 disabled:opacity-40 flex items-center gap-2"
                  data-testid="privacy-submit-email"
                >
                  {emailSaving ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
                  {emailSaving ? 'Sending…' : 'Send confirmation link'}
                </button>
              </div>
            </div>
          )}
        </GlassCard>

        {/* Personal data controls */}
        <GlassCard className="p-6" data-testid="privacy-data-card">
          <div className="flex items-start gap-4 mb-5">
            <GradientIconTile icon={Lock} ramp="fuchsia" />
            <div>
              <h2 className="text-white font-bold font-syne">Your personal data</h2>
              <p className="text-xs text-white/55 mt-0.5">
                Download a copy any time, opt out of CCPA-style data sharing, or request full deletion.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={downloadData}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-colors"
              data-testid="privacy-download-data"
            >
              <span className="flex items-center gap-3">
                <Download size={16} className="text-[#A78BFA]" />
                <span className="text-sm font-medium text-white">Download my data</span>
              </span>
              <span className="text-[11px] text-white/50">JSON</span>
            </button>

            <a
              href="/do-not-sell"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-colors"
              data-testid="privacy-do-not-sell"
            >
              <span className="flex items-center gap-3">
                <Ban size={16} className="text-[#A78BFA]" />
                <span className="text-sm font-medium text-white">Do not sell my information</span>
              </span>
              <span className="text-[11px] text-white/50">CCPA</span>
            </a>

            <button
              onClick={requestDeletion}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-red-500/10 border border-red-500/20 hover:bg-red-500/15 transition-colors"
              data-testid="privacy-request-deletion"
            >
              <span className="flex items-center gap-3">
                <Trash2 size={16} className="text-[#FF6B6B]" />
                <span className="text-sm font-medium text-[#FF6B6B]">Request full account deletion</span>
              </span>
              <span className="text-[11px] text-white/50">GDPR</span>
            </button>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
